import {
  backendRequest as applicationBackendRequest,
  backendRequestCompleted as applicationBackendRequestCompleted,
  preDownloadMessages
} from '../actions/application';
import {
  backendRequest,
  backendRequestCompleted,
  deleteFromCache,
  lockMessages,
  setFolderCache,
  setSelected,
  unlockMessages,
  updateCache,
  updateCacheIfExist,
  backendRequestReset
} from '../actions/messages';
import {updateFolder, updateParentFolder} from '../actions/folders';
import {
  abortControllerWrappers,
  abortFetch,
  apiFetch,
  credentialsHeaders,
  currentCredentials,
  refreshFromHeaders,
  renewSession,
  toJson
} from './fetch';
import {getStore} from '../store';
import {persistMessageCache} from './indexed-db';
import {notifyNewMail} from './notification';
import {FolderTypes} from './folder';
import {URLS} from './url';
import sanitize from './sanitize';
import { toApiProxyUrl, repairMessageMojibake } from '../utils/utils';
import { deleteEmailElasticSearch, insertMailElasticSearch } from './sync-imap';

const _eventSourceWrappers = {};

function _closeEventSource(dispatch, es) {
  if (es && es.close) {
    es.close();
    if (!es.DISPATCHED) {
      dispatch(backendRequestCompleted());
      es.DISPATCHED = true;
      if (typeof es.resolvePromise === 'function') {
        es.resolvePromise();
      }
    }
  }
}

/**
 * "Aborts" the event source (SSE received stream) to reset folder message cache.
 *
 * This will cancel current message poll.
 *
 * @param dispatch
 * @param folderId
 */
export function closeResetFolderMessagesCacheEventSource(dispatch, folderId) {
  _closeEventSource(dispatch, _eventSourceWrappers[folderId]);
  delete _eventSourceWrappers[folderId];
}

/**
 *
 * @param dispatch
 * @param user
 * @param folder
 */
export async function resetFolderMessagesCache(dispatch, user, folder) {
  if (folder && folder._links) {
    const folderKey = folder.folderId || folder.fullName || folder._links.messages.href;
    // Cierra la SSE anterior para este folder si existe
    if (_eventSourceWrappers[folderKey]) {
      _closeEventSource(dispatch, _eventSourceWrappers[folderKey]);
      delete _eventSourceWrappers[folderKey];
    }
    dispatch(backendRequest());

    const isFolderStillSelected = () => {
      try {
        const selectedFolderId = getStore().getState().application.selectedFolderId;
        return !folder.folderId || selectedFolderId === folder.folderId;
      } catch {
        return true;
      }
    };

    // Return a promise
    return new Promise((resolve) => {
      const openStream = retryOn401 => {
        const credentials = currentCredentials() || user.credentials;
        const allMessages = [];
        let isFirstBatch = true;
        // Prefer EventSourcePolyfill instead of EventSource to allow sending HTTP headers in all browsers
        const es = new window.EventSourcePolyfill(
          toApiProxyUrl(folder._links.messages.href),
          {
            headers: credentialsHeaders(credentials)
          });
        _eventSourceWrappers[folderKey] = es;
        // Store resolve function that will be called in _closeEventSource
        es.resolvePromise = resolve;
        // PEC-8747: el token renovado también viaja en las cabeceras de la respuesta del stream
        es.onopen = e => refreshFromHeaders(e && e.headers);
        es.onmessage = e => {
          const messages = JSON.parse(e.data).map(repairMessageMojibake);
          allMessages.push(...messages);
          // Remove UIDs not included in batch update from store
          const originalUids = messages.map(m => m.uid);
          let maxUid = null;
          let minUid = null;
          if (originalUids.length > 0) {
            maxUid = originalUids.reduce((a, b) => Math.max(a, b));
            minUid = originalUids.reduce((a, b) => Math.min(a, b));
            const completeUidSequence = Array(maxUid - minUid + 1).fill('').map((v, i) => i + minUid);
            const uidsToRemove = completeUidSequence.filter(uid => !originalUids.includes(uid));
            if (uidsToRemove.length > 0) {
              dispatch(deleteFromCache(folder, uidsToRemove.map(uid => ({uid}))));
            }
          }
          dispatch(updateCache(folder, messages));
          // This is the First Batch -> Delete all messages with a higher UID not included in the batch
          if (isFirstBatch) {
            isFirstBatch = false;
            if (maxUid) {
              dispatch(deleteFromCache(folder, [], {from: maxUid + 1}));
            } else {
              // Clear empty folder if first batch has no uids
              dispatch(deleteFromCache(folder, [], {from: 0}));
            }
          }
          // This is the last batch -> persistMessageCache with all new gathered messages
          if (e.lastEventId === '1') {
            _closeEventSource(dispatch, es);
            delete _eventSourceWrappers[folderKey];
            // Remove uids not included in batch (0-minUidInBatch)
            if (minUid) {
              dispatch(deleteFromCache(folder, [], {to: minUid - 1}));
            }
            // Manually persist newest version of message cache
            persistMessageCache(user.id, user.hash, folder, [...allMessages]);
          }
        };
        es.onerror = event => {
          const status = event && event.status;
          const isCurrent = _eventSourceWrappers[folderKey] === es;
          if (status === 401 && retryOn401 && isCurrent && isFolderStillSelected()) {
            // PEC-8747: sesión caducada -> renovar en silencio y reabrir el stream una sola vez
            es.close();
            renewSession(credentials.encrypted).then(renewed => {
              if (renewed && _eventSourceWrappers[folderKey] === es && isFolderStillSelected()) {
                openStream(false);
              } else {
                _closeEventSource(dispatch, es);
                if (_eventSourceWrappers[folderKey] === es) {
                  delete _eventSourceWrappers[folderKey];
                }
              }
            });
            return;
          }
          // Cualquier otro error: cerrar, resolver la promesa y liberar el indicador de carga
          _closeEventSource(dispatch, es);
          if (isCurrent) {
            delete _eventSourceWrappers[folderKey];
          }
        };
      };
      openStream(true);
    });
  }
  return null;
}


export function preloadMessages(dispatch, credentials, folder, messageUids) {
  if (messageUids.length === 0) {
    return;
  }

  const proxyUrl = toApiProxyUrl(folder._links.messages.href);
  const absoluteUrl = `${window.location.origin}${proxyUrl}`;
  const url = new URL(absoluteUrl);
  messageUids.forEach(messageUid => url.searchParams.append('id', messageUid));

  apiFetch(url, {
    method: 'GET',
    headers: credentialsHeaders(credentials)
  })
    .then(toJson)
    .then(messages => {
      if (folder.type === FolderTypes.INBOX && messages.some(m => m.recent === true)) {
        notifyNewMail();
      }
      dispatch(preDownloadMessages(messages));
    })
    .catch(() => {/* Ignore any preload error */});
}

export function downloadAttachment(credentials, attachment) {
  const fetch$ = apiFetch(toApiProxyUrl(attachment._links.download.href), {
    method: 'GET',
    headers: credentialsHeaders(credentials)
  }).then(response => response.blob());
  fetch$
    .then(blob => {
      if (navigator.msSaveBlob) {
        navigator.msSaveBlob(blob, attachment.fileName);
      } else {
        const url = URL.createObjectURL(blob);
        const tempLink = document.createElement('a');
        tempLink.href = url;
        tempLink.download = attachment.fileName;
        document.body.appendChild(tempLink);
        tempLink.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));
        document.body.removeChild(tempLink);
        URL.revokeObjectURL(url);
      }
    });
  return fetch$;
}

export function downloadAttachmentAll(credentials, attachment) {
  const fetch$ = apiFetch(toApiProxyUrl(attachment._links.download.href), {
    method: 'GET',
    headers: credentialsHeaders(credentials)
  }).then(response => response.blob());
  fetch$
    .then(blob => {
      const url = URL.createObjectURL(blob);
      const tempLink = document.createElement('a');
      tempLink.href = url;
      tempLink.download = 'archivo.zip';
      document.body.appendChild(tempLink);
      tempLink.dispatchEvent(new MouseEvent('click', {bubbles: true, cancelable: true, view: window}));
      document.body.removeChild(tempLink);
      URL.revokeObjectURL(url);
    });
  return fetch$;
}


/**
 * Moves the provided array of messages from the original fromFolder to the target toFolder.
 *
 * This method is optimistic and will calculate fromFolder message counts before any server response is received.
 *
 * @param dispatch {!function}
 * @param credentials {!Object}
 * @param fromFolder {!Object}
 * @param toFolder {!Object}
 * @param messages {!Array.<Object>}
 */
export function moveMessages(dispatch, credentials, fromFolder, toFolder, messages, user, lexon) {
  // Abort any operations that can affect operation result
  _closeEventSource(dispatch, _eventSourceWrappers.resetFolderMessagesCache);
  abortFetch(abortControllerWrappers.getFoldersAbortController);

  const es = new window.EventSourcePolyfill(toApiProxyUrl(toFolder._links.messages.href),
    {
      headers: credentialsHeaders(credentials)
    });
  let allMessages = [];
  es.onmessage = e => {
    const existMessages = JSON.parse(e.data).map(repairMessageMojibake);
    allMessages = [...existMessages, ...messages];
    persistMessageCache(user.id, user.hash, toFolder, [...allMessages]);
    es._close();
  };
  const fromFolderUpdated = {
    ...fromFolder
  };
  const toFolderUpdated = {
    ...toFolder
  };
  messages.forEach(m => {
    dispatch(setSelected([m], false));
    // Calculate new fromFolder values
    fromFolderUpdated.messageCount -= 1;
    fromFolderUpdated.unreadMessageCount -= (m.seen ? 0 : 1);
    fromFolderUpdated.newMessageCount -= (m.recent ? 1 : 0);
    toFolderUpdated.messageCount += 1;
    toFolderUpdated.unreadMessageCount += (m.seen ? 0 : 1);
    toFolderUpdated.newMessageCount += (m.recent ? 1 : 0);
  });
  // Update state with expected response from server
  dispatch(deleteFromCache(fromFolder, messages));
  dispatch(updateParentFolder(fromFolderUpdated));
  dispatch(lockMessages(messages));
  dispatch(applicationBackendRequest());
  
  return apiFetch(toApiProxyUrl(fromFolder._links['message.move.bulk'].href.replace('{toFolderId}', toFolder.folderId)), {
    method: 'PUT',
    headers: credentialsHeaders(credentials, {'Content-Type': 'application/json'}),
    body: JSON.stringify(messages.map(m => m.uid))
  })
    .then(toJson)
    .then(newMessages => {
      // Update UI as soon as the IMAP move is confirmed (do not wait for ElasticSearch)
      dispatch(unlockMessages(messages));
      if (Array.isArray(newMessages)) {
        dispatch(updateCache(toFolder, newMessages.map(repairMessageMojibake)));
        dispatch(updateParentFolder(toFolderUpdated));
        syncMovedMessagesElasticSearch(lexon, fromFolder, messages, toFolder, newMessages);
      }
    })
    .catch(() => {
      dispatch(unlockMessages(messages));
      // Rollback state from dispatched expected responses
      dispatch(updateCache(fromFolder, messages));
      //dispatch(updateFolder(fromFolder));
      dispatch(updateParentFolder(fromFolder));
    }).finally(() => {
      dispatch(applicationBackendRequestCompleted());
    });
}

/**
 * Fire-and-forget ElasticSearch sync after a move: deletes are fired in parallel and
 * inserts run in parallel too. Never blocks the UI nor triggers a rollback on failure.
 */
function syncMovedMessagesElasticSearch(lexon, fromFolder, messages, toFolder, newMessages) {
  messages.forEach(item => {
    deleteEmailElasticSearch({
      ...lexon,
      folder: fromFolder.fullName,
      id: item.uid
    });
  });
  newMessages.forEach(newMessage => {
    insertMailElasticSearch({
      ...lexon,
      folder: (newMessage.folder && newMessage.folder.fullName) || toFolder.fullName,
      id: newMessage.uid
    }).catch(() => {/* already logged in service */});
  });
}

export function setMessagesSeen(dispatch, credentials, folder, messages, seen) {
  // Abort any operations that can affect operation result
  _closeEventSource(dispatch, _eventSourceWrappers.resetFolderMessagesCache);
  abortFetch(abortControllerWrappers.getFoldersAbortController);

  const messagesToUpdate = [];
  // Update state with expected response from server
  const expectedUpdatedFolder = {...folder};
  messages.forEach(m => {
    // Calculate new folder values
    messagesToUpdate.push({...m, seen: seen});
    expectedUpdatedFolder.unreadMessageCount += seen ? -1 : +1;
  });
  dispatch(updateCacheIfExist(folder, messagesToUpdate, true));
  dispatch(updateFolder(expectedUpdatedFolder));
  dispatch(lockMessages(messagesToUpdate));

  apiFetch(toApiProxyUrl(folder._links['message.seen.bulk'].href.replace('{seen}', seen.toString())), {
    method: 'PUT',
    headers: credentialsHeaders(credentials, {'Content-Type': 'application/json'}),
    body: JSON.stringify(messagesToUpdate.map(m => m.uid))
  })
    .then(response => {
      dispatch(unlockMessages(messagesToUpdate));
      if (!response.ok) {
        // Rollback state from dispatched expected responses
        dispatch(updateCacheIfExist(folder, messages));
        dispatch(updateFolder(folder));
      }
    });
}

export function setMessageFlagged(dispatch, credentials, folder, message, flagged) {
  // Abort any operations that can affect operation result
  _closeEventSource(dispatch, _eventSourceWrappers.resetFolderMessagesCache);
  abortFetch(abortControllerWrappers.getFoldersAbortController);

  dispatch(updateCacheIfExist(folder, [{...message, flagged}], true));
  dispatch(lockMessages([message]));
  apiFetch(toApiProxyUrl(folder._links['message.flagged'].href.replace('{messageId}', message.uid)), {
    method: 'PUT',
    headers: credentialsHeaders(credentials, {'Content-Type': 'application/json'}),
    body: JSON.stringify(flagged)
  })
    .then(response => {
      dispatch(unlockMessages([message]));
      if (!response.ok) {
        // Rollback state from dispatched expected responses
        dispatch(updateCacheIfExist(folder, [message]));
      }
    });
}

export async function deleteAllFolderMessages(dispatch, credentials, folder) {
  // Abort any operations that can affect operation result
  _closeEventSource(dispatch, _eventSourceWrappers.resetFolderMessagesCache);
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  dispatch(applicationBackendRequest());
  try {
    const proxyUrl = toApiProxyUrl(folder._links.messages.href);
    const absoluteUrl = `${window.location.origin}${proxyUrl}`;
    const url = new URL(absoluteUrl);
    const updatedFolder = await apiFetch(url, {
      method: 'DELETE',
      headers: credentialsHeaders(credentials)
    }).then(toJson);
    dispatch(updateFolder(updatedFolder));
    dispatch(setFolderCache(updatedFolder, []));
  } catch (e) {
    console.error("Error deleting all messages", e);
  } finally {
    dispatch(applicationBackendRequestCompleted());
  }
}

export function deleteMessages(dispatch, credentials, folder, messages, lexon) {
  if (messages.length === 0) {
    return;
  }
  // Abort any operations that can affect operation result
  _closeEventSource(dispatch, _eventSourceWrappers.resetFolderMessagesCache);
  abortFetch(abortControllerWrappers.getFoldersAbortController);
  const proxyUrl = toApiProxyUrl(folder._links.messages.href);
  const absoluteUrl = `${window.location.origin}${proxyUrl}`;
  const url = new URL(absoluteUrl);
  const messagesToDelete = [];
  // Update state with expected response from server
  const expectedUpdatedFolder = {...folder};
  messages.forEach(message => {
    url.searchParams.append('id', message.uid);
    messagesToDelete.push({...message, deleted: true});
    expectedUpdatedFolder.deletedMessageCount++;
    expectedUpdatedFolder.unreadMessageCount = message.seen ? expectedUpdatedFolder.unreadMessageCount
      : expectedUpdatedFolder.unreadMessageCount - 1;
    expectedUpdatedFolder.newMessageCount = message.recent ? expectedUpdatedFolder.newMessageCount - 1
      : expectedUpdatedFolder.newMessageCount;
  });
  dispatch(updateCacheIfExist(folder, messagesToDelete, true));
  dispatch(setSelected(messagesToDelete, false));
  dispatch(updateFolder(expectedUpdatedFolder));
  dispatch(lockMessages(messagesToDelete));
  return apiFetch(url, {
    method: 'DELETE',
    headers: credentialsHeaders(credentials)
  })
    .then(toJson)
    .then(updatedFolder => {
      // Fire-and-forget: update ElasticSearch without blocking UI or showing loaders
      messages.forEach(item => {
        try {
          deleteEmailElasticSearch({
            ...lexon,
            folder: folder.fullName,
            id: item.uid
          });
        } catch (e) {
          console.error('Error deleting email in ElasticSearch', e);
        }
      });
      dispatch(unlockMessages(messagesToDelete));
      dispatch(updateFolder(updatedFolder));
      dispatch(deleteFromCache(updatedFolder, messagesToDelete));
    })
    .catch(() => {
      dispatch(unlockMessages(messagesToDelete));
      // Rollback state from dispatched expected responses
      dispatch(updateCacheIfExist(folder, messages));
      dispatch(updateFolder(folder));
    });
}

export function getFilterMessage(credentials, folder, filter, type, timeout = 20000) {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeout);

  const response = apiFetch(`${URLS.FOLDERS}/${folder}/messages/search?type=${type}`, {
    method: 'POST',
    headers: credentialsHeaders(credentials),
    body: filter,
    signal: abortController.signal
  })
  .then(toJson)
  .then(result => {
        clearTimeout(timeoutId);
        return result && result.length > 0 
        ? result.map(item => item.messageId) 
        : [];
      }
    )
  .catch( error => {
    clearTimeout(timeoutId);
    if (error.name === "AbortError"){
      throw new Error('La solicitud ha tardado demasiado y se ha cancelado.');
    }
    throw error;
  });
  

  return response;
}

export function getFilterMessageOnlyIdsNumber(credentials, folder, filter, type, timeout = 20000) {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeout);

  const response = apiFetch(`${URLS.FOLDERS}/${folder}/native-messages-id/search?type=${type}`, {
    method: 'POST',
    headers: credentialsHeaders(credentials),
    body: filter,
    signal: abortController.signal
  })
  .then(toJson)    
  .then(result => {
    return result && result.length > 0 
    ? result.map(item => item.messageId) 
    : [];
  })
  .catch( error => {
    clearTimeout(timeoutId);
    if (error.name === "AbortError"){
      throw new Error('La solicitud ha tardado demasiado y se ha cancelado.');
    }
    throw error;
  });

  return response;
}

export function getMessageByIdsNumber(credentials, folder, ids, timeout = 20000) {
  const abortController = new AbortController();
  const timeoutId = setTimeout(() => abortController.abort(), timeout);

  const response = apiFetch(`${URLS.FOLDERS}/${folder}/native-messages?id=${ids}`, {
    method: 'GET',
    headers: credentialsHeaders(credentials),
    signal: abortController.signal
  })
  .then(toJson) 
  .then(result => {
    return result && result.length > 0 
    ? result.map(item => item.messageId) 
    : [];
  })
  .catch( error => {
    clearTimeout(timeoutId);
    if (error.name === "AbortError"){
      throw new Error('La solicitud ha tardado demasiado y se ha cancelado.');
    }
    throw error;
  });

  return response;
}

export function addSignAndTemplateToMessage(originalContent, lexon) {
  const defaultSign = lexon.firmas?.find(sign => sign.isDefault);
  const defaultTemplate = lexon.templates?.find(template => template.isDefault);
  let content = sanitize.sanitize(originalContent);
  if (defaultSign) {
    content =
      `<br/><br/><p>${defaultSign.sign}</p>${content}`;
  }
  if (defaultTemplate) {
    content = `<br/><br/><p>${defaultTemplate.template}</p>${content}`;
  }
  return content;
}

export function handleRackendRequestReset(dispatch) {
  dispatch(backendRequestReset());
}

export async function downloadAttachmentFile(credentials, attachment) {
  try {
    const response = await apiFetch(toApiProxyUrl(attachment._links.download.href), {
      method: 'GET',
      headers: credentialsHeaders(credentials)
    });
    if (!response.ok) {
      throw new Error('Failed to download attachment the file');
    }
    const blob = await response.blob();
    const file = new File([blob], attachment.fileName, {type: blob.type});
    return file;
  } catch (error) {
    console.error('Error downloading attachment file:', error);
    return null;
  }
}

// True when the converted HTML has text besides images (PEC-8727).
const htmlHasVisibleText = html => html
  .replace(/<(script|style|title)[\s\S]*?<\/\1>/gi, '')
  .replace(/<[^>]*>/g, '')
  .replace(/&nbsp;|\s/g, '')
  .length > 0;

export async function convertDocToHtml(file) {
  const formData = new FormData();
  formData.append('file', file);

  try {
    const response = await fetch(
      `${window.API_GATEWAY}/api/v1/utils/Aspose/convertDocToHtml`,
      {
        method: 'POST',
        body: formData
      }
    );

    if (!response.ok) {
      throw new Error('Failed to convert document to HTML');
    }

    const result = await response.json();
    const html = result?.data;

    if (typeof html !== 'string' || html.length === 0) {
      throw new Error('Invalid HTML response from document conversion service');
    }

    // Extract base64 image data URLs from the HTML response when available.
    const base64Matches = [...html.matchAll(/data:image\/[a-zA-Z]+;base64,[^"]+/g)];

    // Mixed text + images: keep images empty so the preview renders the HTML.
    return {
      images: htmlHasVisibleText(html) ? [] : base64Matches.map(match => match[0]),
      html
    };
  } catch (error) {
    console.error('Error converting document:', error);
    return {
      images: [],
      html: ''
    };
  }
}

/**
 * Search emails in ElasticSearch via Lefebvre API Gateway.
 * @param {string} userId - Lefebvre credential (e.g. "E1654461")
 * @param {string} email - Email account (e.g. "lexon.quality@gmail.com")
 * @param {object} body - Search body (e.g. { Folder: "INBOX", ... })
 * @param {object} options - { from, size, sort_order, sort_field, token }
 * @returns {Promise<object>} ElasticSearch response
 */
export async function searchMailsElastic(dispatch, userId, email, body, {
  from = 0,
  size = 50,
  sort_order = 'Desc',
  sort_field = 'date',
  lexonToken
}) {
  try {
    dispatch(applicationBackendRequest());
    const url = `${window.API_GATEWAY_ACC}/api/v1/imap/ElasticSearch/${userId}/SearchMailsImap/${encodeURIComponent(email)}?from=${from}&size=${size}&sort_order=${sort_order}&sort_field=${sort_field}`;

    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'text/plain',
        'Content-Type': 'application/json',
        ...(lexonToken ? { Authorization: `Bearer ${lexonToken}` } : {})
      },
      body: JSON.stringify(body)
    });

    if (!response.ok) {
      throw new Error(`ElasticSearch error: ${response.status} ${response.statusText}`);
    }
    const json = await response.json();

    // Deduplicate items by (metadata.idExternal, metadata.messageId)
    try {
      if (json && json.data && Array.isArray(json.data.items)) {
        const seen = new Set();
        const deduped = [];
        for (const item of json.data.items) {
          const meta = item && item.metadata;
          const idExternal = meta && meta.idExternal;
          const messageId = meta && meta.messageId;
          if (idExternal !== null && typeof idExternal !== 'undefined' && messageId !== null && typeof messageId !== 'undefined') {
            const key = `${idExternal}||${messageId}`;
            if (seen.has(key)) {
              continue;
            }
            seen.add(key);
          }
          deduped.push(item);
        }
        json.data.items = deduped;
      }
    } catch (e) {
      // Non-fatal: if deduplication fails, return original payload
      console.warn('Deduplication step skipped due to error:', e);
      return json;
    }
    return json;
  } catch (error) {
    console.error('ElasticSearch search error:', error);
    dispatch(applicationBackendRequestCompleted());
    throw error;
  } finally {
    dispatch(applicationBackendRequestCompleted());
  }
}

/**
 * Fetch read/unread statuses for multiple folders and message ids.
 * @param {string} userId - Lefebvre credential
 * @param {string} email - Email account
 * @param {Array<{folder:string, ids:number[]}>} folders - Folders with ids
 * @param {string} lexonToken - Bearer token
 * @returns {Promise<Array<{folder:string,idExternal:number,isRead:boolean}>>}
 */
export async function getReadStatuses(dispatch, userId, email, folders, lexonToken) {
  try {
    dispatch(applicationBackendRequest());
    const url = `${window.API_GATEWAY_ACC}/api/v1/imap/mail/getReadMessage`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        accept: 'text/plain',
        'Content-Type': 'application/json-patch+json',
        ...(lexonToken ? { Authorization: `Bearer ${lexonToken}` } : {})
      },
      body: JSON.stringify({
        lefebvreCredential: userId,
        email,
        folders
      })
    });
    if (!response.ok) {
      throw new Error(`ReadStatus error: ${response.status} ${response.statusText}`);
    }
    const result = await response.json();
    // Some gateways wrap in { data }
    return Array.isArray(result) ? result : (result?.data || []);
  } catch (error) {
    console.error('Fetch read statuses error:', error);
    dispatch(applicationBackendRequestCompleted());
    return [];
  } finally {
    dispatch(applicationBackendRequestCompleted());
  }
}

export const downloadWinmailDatFiles = async (
  token,
  credentials,
  file,
) => {
  const formData = new FormData();
  const responseFile = await apiFetch(toApiProxyUrl(file._links.download.href), {
    method: 'GET',
    headers: credentialsHeaders(credentials)
  });
  const blob = await responseFile.blob();
  formData.append("file", blob, file.fileName);
  const response =
    await fetch(`${window.API_GATEWAY_ACC}/api/v1/imap/Attachment/GetAttachmentsFromWinmailDat`,
      {
        method: "POST",
        headers: {
          Accept: "text/plain",
          Authorization: `Bearer ${token}`
        },
        body: formData
      });
  const result = await response.json();
  return result.data;
};
